import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  X,
  Loader2,
  Copy,
  Save,
  AlertCircle,
  ChevronRight,
} from 'lucide-react';
import api from '../../utils/api';

/**
 * ProductDetailModal — ficha técnica completa de un producto (superadmin / EXTENDED MODE).
 *
 * 6 tabs con lazy load (la data se fetcha al primer click sobre cada tab):
 *   1. General        → row de la toma actual (filtrada por id_catalogo)
 *   2. Catalog State  → row de la tabla catalog (cliente_id + id_catalogo)
 *   3. Sources        → fuentes de hidratación (toma raw, mp, tcc, tags, decorator winner)
 *   4. History        → eventos de catalog_history para el id
 *   5. Tomas Appearance → últimas N tomas donde apareció (default 5 + "ver todas")
 *   6. Raw JSON       → todo agregado, con Copy / Save to disk
 *
 * Cada tab maneja su propio loading/error sin tumbar el modal entero.
 */

interface ProductDetailModalProps {
  idCatalogo: string;
  clientId: string;
  tableName: string; // toma_* a usar para el Tab 1 (General)
  onClose: () => void;
}

type TabKey =
  | 'general'
  | 'catalog'
  | 'sources'
  | 'history'
  | 'tomas'
  | 'raw';

interface TabState<T = any> {
  loading: boolean;
  loaded: boolean;
  data: T | null;
  error: string | null;
}

function emptyState<T = any>(): TabState<T> {
  return { loading: false, loaded: false, data: null, error: null };
}

const isDevelopment = process.env.NODE_ENV === 'development';
const SAVE_DUMP_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL
  : (isDevelopment ? 'http://localhost:3001' : '');
const SAVE_DUMP_ENDPOINT = `${SAVE_DUMP_BASE_URL}/api/debug/save-perf-dump`;

// =====================================================================
// Helpers de render
// =====================================================================

function KV({ k, v }: { k: string; v: any }) {
  const rendered = (() => {
    if (v === null || v === undefined) return <span className="text-gray-400 italic">null</span>;
    if (typeof v === 'boolean') return <span className="font-mono">{v ? 'true' : 'false'}</span>;
    if (typeof v === 'object') {
      try {
        return (
          <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all max-h-60">
            {JSON.stringify(v, null, 2)}
          </pre>
        );
      } catch {
        return <span className="text-red-500">[unserializable]</span>;
      }
    }
    const s = String(v);
    if (s.length > 300) {
      return (
        <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all max-h-60">
          {s}
        </pre>
      );
    }
    return <span className="break-all">{s}</span>;
  })();
  return (
    <div className="grid grid-cols-[180px_1fr] gap-2 py-1 border-b border-gray-100 text-xs">
      <div className="text-gray-500 font-mono">{k}</div>
      <div className="text-gray-800">{rendered}</div>
    </div>
  );
}

function ObjectKVList({ obj }: { obj: Record<string, any> | null | undefined }) {
  if (!obj) return <div className="text-gray-500 italic text-sm">Sin datos</div>;
  const keys = Object.keys(obj);
  if (keys.length === 0) return <div className="text-gray-500 italic text-sm">Sin datos</div>;
  return (
    <div>
      {keys.map((k) => (
        <KV key={k} k={k} v={obj[k]} />
      ))}
    </div>
  );
}

function ErrorBlock({ error }: { error: string }) {
  return (
    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">
      <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="font-semibold">Error cargando</div>
        <pre className="text-[11px] mt-1 whitespace-pre-wrap break-all font-mono">{error}</pre>
      </div>
    </div>
  );
}

/**
 * Devuelve un mensaje de error verbose con request URL, status, message del backend y detalles.
 * Pensado para diagnóstico runtime — el modal es debug-only (EXTENDED MODE).
 */
function formatFetchError(err: any, requestUrl?: string): string {
  const parts: string[] = [];
  if (requestUrl) parts.push(`URL: ${requestUrl}`);
  const status = err?.response?.status;
  if (status !== undefined) parts.push(`Status: ${status}`);
  const respData = err?.response?.data;
  if (respData) {
    if (typeof respData === 'string') {
      parts.push(`Response: ${respData}`);
    } else {
      if (respData.error) parts.push(`Backend error: ${respData.error}`);
      if (respData.details) parts.push(`Details: ${respData.details}`);
      if (respData.code) parts.push(`Code: ${respData.code}`);
      // Si no tuvo ninguno de los conocidos, volcar el JSON entero.
      if (!respData.error && !respData.details && !respData.code) {
        try {
          parts.push(`Response: ${JSON.stringify(respData)}`);
        } catch {
          parts.push('Response: [unserializable]');
        }
      }
    }
  }
  if (err?.message) parts.push(`Message: ${err.message}`);
  if (err?.code) parts.push(`AxiosCode: ${err.code}`);
  if (parts.length === 0) parts.push('Error desconocido (sin response ni message)');
  return parts.join('\n');
}

function LoadingBlock({ label = 'Cargando…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-500 p-4">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span>{label}</span>
    </div>
  );
}

// =====================================================================
// Modal principal
// =====================================================================

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({
  idCatalogo,
  clientId,
  tableName,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<TabKey>('general');

  const [general, setGeneral] = useState<TabState>(emptyState());
  const [catalog, setCatalog] = useState<TabState>(emptyState());
  const [sources, setSources] = useState<TabState>(emptyState());
  const [history, setHistory] = useState<TabState>(emptyState());
  const [tomas, setTomas] = useState<TabState>(emptyState());
  const [tomasShowAll, setTomasShowAll] = useState(false);
  const [tomasLoadingAll, setTomasLoadingAll] = useState(false);

  // Save to disk feedback
  const [saving, setSaving] = useState(false);
  const [savedPath, setSavedPath] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied'>('idle');

  const safeId = useMemo(() => encodeURIComponent(idCatalogo), [idCatalogo]);
  const cid = useMemo(() => encodeURIComponent(clientId), [clientId]);

  // ---------------- Fetchers (memo + idempotent) ----------------

  const fetchGeneral = useCallback(async () => {
    setGeneral((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.get(`/api/tables/${encodeURIComponent(tableName)}/data`, {
        params: { id_catalogo: idCatalogo, limit: 1 },
      });
      const rows = res.data?.data || [];
      const row = rows[0] || null;
      setGeneral({ loading: false, loaded: true, data: row, error: null });
    } catch (err: any) {
      setGeneral({
        loading: false,
        loaded: true,
        data: null,
        error: formatFetchError(err, `/api/tables/${tableName}/data?id_catalogo=${idCatalogo}&limit=1`),
      });
    }
  }, [tableName, idCatalogo]);

  const fetchCatalog = useCallback(async () => {
    setCatalog((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.get(`/api/clients/${cid}/catalog/${safeId}`);
      setCatalog({ loading: false, loaded: true, data: res.data?.row || null, error: null });
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 404) {
        // Cliente sin catalog poblado para este id — gracefully degradar.
        setCatalog({ loading: false, loaded: true, data: null, error: null });
      } else {
        setCatalog({
          loading: false,
          loaded: true,
          data: null,
          error: formatFetchError(err, `/api/clients/${cid}/catalog/${safeId}`),
        });
      }
    }
  }, [cid, safeId]);

  const fetchSources = useCallback(async () => {
    setSources((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.get(`/api/clients/${cid}/catalog/${safeId}/sources`);
      setSources({ loading: false, loaded: true, data: res.data || null, error: null });
    } catch (err: any) {
      setSources({
        loading: false,
        loaded: true,
        data: null,
        error: formatFetchError(err, `/api/clients/${cid}/catalog/${safeId}/sources`),
      });
    }
  }, [cid, safeId]);

  const fetchHistory = useCallback(async () => {
    setHistory((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await api.get(`/api/clients/${cid}/catalog/${safeId}/history`);
      setHistory({ loading: false, loaded: true, data: res.data || null, error: null });
    } catch (err: any) {
      setHistory({
        loading: false,
        loaded: true,
        data: null,
        error: formatFetchError(err, `/api/clients/${cid}/catalog/${safeId}/history`),
      });
    }
  }, [cid, safeId]);

  const fetchTomas = useCallback(
    async (limit: number) => {
      if (limit > 5) setTomasLoadingAll(true);
      else setTomas((s) => ({ ...s, loading: true, error: null }));
      try {
        const res = await api.get(
          `/api/clients/${cid}/catalog/${safeId}/tomas-appearance`,
          { params: { limit } }
        );
        setTomas({ loading: false, loaded: true, data: res.data || null, error: null });
        if (limit > 5) setTomasShowAll(true);
      } catch (err: any) {
        setTomas({
          loading: false,
          loaded: true,
          data: null,
          error: formatFetchError(err, `/api/clients/${cid}/catalog/${safeId}/tomas-appearance?limit=${limit}`),
        });
      } finally {
        setTomasLoadingAll(false);
      }
    },
    [cid, safeId]
  );

  // ---------------- Lazy load per tab ----------------

  useEffect(() => {
    if (activeTab === 'general' && !general.loaded && !general.loading) fetchGeneral();
    if (activeTab === 'catalog' && !catalog.loaded && !catalog.loading) fetchCatalog();
    if (activeTab === 'sources' && !sources.loaded && !sources.loading) fetchSources();
    if (activeTab === 'history' && !history.loaded && !history.loading) fetchHistory();
    if (activeTab === 'tomas' && !tomas.loaded && !tomas.loading) fetchTomas(5);
  }, [
    activeTab,
    general,
    catalog,
    sources,
    history,
    tomas,
    fetchGeneral,
    fetchCatalog,
    fetchSources,
    fetchHistory,
    fetchTomas,
  ]);

  // Para Tab Raw JSON: cargar TODO de una si todavía no fue cargado.
  const ensureAllForRaw = useCallback(() => {
    if (!general.loaded && !general.loading) fetchGeneral();
    if (!catalog.loaded && !catalog.loading) fetchCatalog();
    if (!sources.loaded && !sources.loading) fetchSources();
    if (!history.loaded && !history.loading) fetchHistory();
    if (!tomas.loaded && !tomas.loading) fetchTomas(5);
  }, [
    general,
    catalog,
    sources,
    history,
    tomas,
    fetchGeneral,
    fetchCatalog,
    fetchSources,
    fetchHistory,
    fetchTomas,
  ]);

  useEffect(() => {
    if (activeTab === 'raw') ensureAllForRaw();
  }, [activeTab, ensureAllForRaw]);

  // ESC para cerrar
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // ---------------- Raw JSON build ----------------

  const buildRawPayload = useCallback(() => {
    return {
      _meta: {
        idCatalogo,
        clientId,
        tableName,
        capturedAt: new Date().toISOString(),
      },
      general: { loaded: general.loaded, error: general.error, data: general.data },
      catalog: { loaded: catalog.loaded, error: catalog.error, data: catalog.data },
      sources: { loaded: sources.loaded, error: sources.error, data: sources.data },
      history: { loaded: history.loaded, error: history.error, data: history.data },
      tomasAppearance: { loaded: tomas.loaded, error: tomas.error, data: tomas.data },
    };
  }, [idCatalogo, clientId, tableName, general, catalog, sources, history, tomas]);

  const handleCopy = useCallback(() => {
    const payload = buildRawPayload();
    const json = JSON.stringify(payload, null, 2);
    try {
      navigator.clipboard.writeText(json).then(() => {
        setCopyStatus('copied');
        window.setTimeout(() => setCopyStatus('idle'), 2000);
      });
    } catch {
      window.prompt('Copiá el JSON manualmente:', json);
    }
  }, [buildRawPayload]);

  const handleSaveToDisk = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    setSavedPath(null);
    try {
      const payload = buildRawPayload();
      const context = `product-${idCatalogo}`.replace(/[^a-zA-Z0-9_-]/g, '').slice(0, 40);
      const res = await fetch(SAVE_DUMP_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await (async () => {
            try {
              const { auth } = await import('../../config/firebase');
              const u = auth.currentUser;
              return u ? await u.getIdToken(false) : '';
            } catch {
              return '';
            }
          })()}`,
        },
        body: JSON.stringify({ payload, context }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) {
        setSaveError(json?.error || `HTTP ${res.status}`);
      } else {
        setSavedPath(json.savedPath);
        try {
          await navigator.clipboard.writeText(json.savedPath);
        } catch {
          // ignore
        }
      }
    } catch (err: any) {
      setSaveError(err?.message || 'Error desconocido');
    } finally {
      setSaving(false);
    }
  }, [saving, buildRawPayload, idCatalogo]);

  // ---------------- Render ----------------

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'general', label: 'General' },
    { key: 'catalog', label: 'Catalog State' },
    { key: 'sources', label: 'Sources' },
    { key: 'history', label: 'History' },
    { key: 'tomas', label: 'Tomas Appearance' },
    { key: 'raw', label: 'Raw JSON' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="bg-white rounded-lg shadow-2xl flex flex-col"
        style={{ width: '95vw', height: '90vh', maxWidth: '95vw' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-gray-800 text-sm">Product Detail</span>
            <ChevronRight className="w-3 h-3 text-gray-400" />
            <span className="font-mono text-xs text-gray-600 truncate" title={idCatalogo}>
              {idCatalogo}
            </span>
            <span className="text-[10px] text-gray-400 uppercase tracking-wide ml-2">
              {tableName} · {clientId}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded"
            title="Cerrar (Esc)"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-gray-200 text-xs">
          {tabs.map((t) => {
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`px-3 py-2 border-b-2 transition-colors ${
                  active
                    ? 'border-pink-500 text-pink-600 font-semibold bg-pink-50/40'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {activeTab === 'general' && (
            <TabContainer>
              {general.loading && <LoadingBlock />}
              {general.error && <ErrorBlock error={general.error} />}
              {general.loaded && !general.error && (
                general.data ? (
                  <ObjectKVList obj={general.data} />
                ) : (
                  <EmptyBlock label="No se encontró el row para este id_catalogo en la toma actual." />
                )
              )}
            </TabContainer>
          )}

          {activeTab === 'catalog' && (
            <TabContainer>
              {catalog.loading && <LoadingBlock />}
              {catalog.error && <ErrorBlock error={catalog.error} />}
              {catalog.loaded && !catalog.error && (
                catalog.data ? (
                  <CatalogStateView data={catalog.data} />
                ) : (
                  <EmptyBlock label="Sin datos en catalog para este id_catalogo." />
                )
              )}
            </TabContainer>
          )}

          {activeTab === 'sources' && (
            <TabContainer>
              {sources.loading && <LoadingBlock />}
              {sources.error && <ErrorBlock error={sources.error} />}
              {sources.loaded && !sources.error && sources.data && (
                <SourcesView data={sources.data} />
              )}
            </TabContainer>
          )}

          {activeTab === 'history' && (
            <TabContainer>
              {history.loading && <LoadingBlock />}
              {history.error && <ErrorBlock error={history.error} />}
              {history.loaded && !history.error && (
                <HistoryView data={history.data} />
              )}
            </TabContainer>
          )}

          {activeTab === 'tomas' && (
            <TabContainer>
              {tomas.loading && <LoadingBlock />}
              {tomas.error && <ErrorBlock error={tomas.error} />}
              {tomas.loaded && !tomas.error && (
                <TomasAppearanceView
                  data={tomas.data}
                  showAll={tomasShowAll}
                  onLoadAll={() => fetchTomas(999)}
                  loadingAll={tomasLoadingAll}
                />
              )}
            </TabContainer>
          )}

          {activeTab === 'raw' && (
            <RawJsonView
              payload={buildRawPayload()}
              onCopy={handleCopy}
              onSave={handleSaveToDisk}
              copyStatus={copyStatus}
              saving={saving}
              savedPath={savedPath}
              saveError={saveError}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// =====================================================================
// Sub-views
// =====================================================================

const TabContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded border border-gray-200 p-4 max-w-5xl mx-auto">{children}</div>
);

const EmptyBlock: React.FC<{ label: string }> = ({ label }) => (
  <div className="text-gray-500 italic text-sm py-2">{label}</div>
);

/**
 * Vista del Tab Catalog State.
 *
 * Renderiza `image_base64` (si existe) como <img> arriba, en una row dedicada
 * con label "Imagen". El resto de campos se renderiza como KV list — pero
 * `image_base64` queda excluido del list genérico para no duplicar info ni
 * mostrar el string crudo.
 */
function CatalogStateView({ data }: { data: Record<string, any> }) {
  if (!data || Object.keys(data).length === 0) {
    return <EmptyBlock label="Sin datos" />;
  }

  const imageRaw = data.image_base64;
  // Construir src para el <img>:
  //   - Si ya viene con prefijo `data:image/...;base64,...` → usar tal cual.
  //   - Si viene solo el base64 sin prefix → asumir image/jpeg.
  const imageSrc = (() => {
    if (typeof imageRaw !== 'string') return null;
    const trimmed = imageRaw.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('data:image/')) return trimmed;
    return `data:image/jpeg;base64,${trimmed}`;
  })();

  // Resto de campos (sin image_base64).
  const rest: Record<string, any> = {};
  for (const k of Object.keys(data)) {
    if (k === 'image_base64') continue;
    rest[k] = data[k];
  }

  return (
    <div>
      <div className="grid grid-cols-[180px_1fr] gap-2 py-2 border-b border-gray-200 text-xs items-start">
        <div className="text-gray-500 font-mono pt-1">Imagen</div>
        <div className="text-gray-800">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt="producto"
              style={{ maxWidth: 200, maxHeight: 200, objectFit: 'contain' }}
              className="border border-gray-200 rounded bg-white"
            />
          ) : (
            <span className="text-gray-400 italic">Sin imagen</span>
          )}
        </div>
      </div>
      <ObjectKVList obj={rest} />
    </div>
  );
}

function SourcesView({ data }: { data: any }) {
  if (!data) return <EmptyBlock label="Sin datos" />;
  const { tomaActual, matchingProcess, tomaCalcCache, tagsTable, gramajeHelperWinner, notes } = data;
  return (
    <div className="space-y-5">
      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Toma actual (raw)</h3>
        {tomaActual ? <ObjectKVList obj={tomaActual} /> : <EmptyBlock label="No disponible" />}
      </section>

      {Array.isArray(matchingProcess) && (
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">
            matching_process <span className="text-xs text-gray-400 font-normal">({matchingProcess.length})</span>
          </h3>
          {matchingProcess.length === 0 ? (
            <EmptyBlock label="Sin rows en matching_process" />
          ) : (
            <div className="space-y-2">
              {matchingProcess.map((row: any, i: number) => (
                <div key={i} className="border border-gray-200 rounded p-2 bg-gray-50">
                  <ObjectKVList obj={row} />
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {Array.isArray(tomaCalcCache) && (
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">
            toma_calc_cache <span className="text-xs text-gray-400 font-normal">({tomaCalcCache.length})</span>
          </h3>
          {tomaCalcCache.length === 0 ? (
            <EmptyBlock label="Sin rows en toma_calc_cache" />
          ) : (
            <div className="space-y-2">
              {tomaCalcCache.map((row: any, i: number) => (
                <div key={i} className="border border-gray-200 rounded p-2 bg-gray-50">
                  <ObjectKVList obj={row} />
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {tagsTable !== undefined && (
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Tags table</h3>
          {Array.isArray(tagsTable) && tagsTable.length > 0 ? (
            <div className="space-y-2">
              {tagsTable.map((row: any, i: number) => (
                <div key={i} className="border border-gray-200 rounded p-2 bg-gray-50">
                  <ObjectKVList obj={row} />
                </div>
              ))}
            </div>
          ) : (
            <EmptyBlock label="Sin tags table o sin registros para este id" />
          )}
        </section>
      )}

      <section>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">gramajeHelper winner</h3>
        <div className="text-sm text-gray-700 break-all">
          {gramajeHelperWinner ? (
            <span className="font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">
              {String(gramajeHelperWinner)}
            </span>
          ) : (
            <span className="text-gray-400 italic">n/a</span>
          )}
        </div>
      </section>

      {Array.isArray(notes) && notes.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Notas</h3>
          <ul className="list-disc pl-5 text-xs text-gray-600 space-y-1">
            {notes.map((n: string, i: number) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function HistoryView({ data }: { data: any }) {
  if (!data) return <EmptyBlock label="Sin datos" />;
  const events = data.events || [];
  if (data.note && events.length === 0) {
    return <EmptyBlock label={data.note} />;
  }
  if (events.length === 0) {
    return <EmptyBlock label="Sin eventos registrados" />;
  }
  return (
    <div className="space-y-2">
      {events.map((ev: any, i: number) => {
        const eventType = ev.event_type || ev.type || 'evento';
        const ts = ev.created_at || ev.timestamp || '—';
        const actor = ev.actor || ev.actor_type ? `${ev.actor || ''}${ev.actor_type ? ` (${ev.actor_type})` : ''}` : null;
        const payload = ev.payload;
        return (
          <div key={ev.id || i} className="border border-gray-200 rounded p-2 bg-gray-50">
            <div className="flex items-center justify-between text-xs mb-1 flex-wrap gap-1">
              <span className="font-semibold text-pink-700">
                {eventType}
              </span>
              {actor && (
                <span className="text-[10px] text-gray-500 font-mono">{actor}</span>
              )}
              <span className="font-mono text-gray-500 tabular-nums text-[10px]">
                {ts}
              </span>
            </div>
            {payload !== undefined && payload !== null && (
              <pre className="text-[11px] bg-white border border-gray-200 rounded p-1.5 overflow-x-auto whitespace-pre-wrap break-all max-h-60 mt-1">
                {typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2)}
              </pre>
            )}
          </div>
        );
      })}
      {data.has_more && (
        <div className="text-[10px] text-gray-400 italic mt-2">Hay más eventos — esta vista carga los primeros 50.</div>
      )}
    </div>
  );
}

function TomasAppearanceView({
  data,
  showAll,
  onLoadAll,
  loadingAll,
}: {
  data: any;
  showAll: boolean;
  onLoadAll: () => void;
  loadingAll: boolean;
}) {
  if (!data) return <EmptyBlock label="Sin datos" />;
  const rows = data.appearances || [];
  if (rows.length === 0) {
    return <EmptyBlock label="No aparece en ninguna toma del rango consultado." />;
  }
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-gray-600">
          <span className="font-semibold text-gray-800 tabular-nums">{rows.length}</span> tomas
          {data.totalScanned ? (
            <span className="text-gray-400 ml-2">
              (escaneadas {data.totalScanned}{data.limit ? ` · limit ${data.limit}` : ''})
            </span>
          ) : null}
        </div>
        {!showAll && (
          <button
            type="button"
            onClick={onLoadAll}
            disabled={loadingAll}
            className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            {loadingAll ? 'Cargando…' : 'Ver todas'}
          </button>
        )}
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="w-full text-xs">
          <thead className="bg-gray-100">
            <tr>
              <th className="text-left px-2 py-1.5 font-semibold text-gray-700">Toma</th>
              <th className="text-left px-2 py-1.5 font-semibold text-gray-700">Fecha</th>
              <th className="text-right px-2 py-1.5 font-semibold text-gray-700">sale_price</th>
              <th className="text-left px-2 py-1.5 font-semibold text-gray-700">Sitio</th>
              <th className="text-right px-2 py-1.5 font-semibold text-gray-700">Página</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r: any, i: number) => (
              <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-2 py-1 font-mono">toma_{r.tomaN}</td>
                <td className="px-2 py-1 tabular-nums">{r.fecha || '—'}</td>
                <td className="px-2 py-1 tabular-nums text-right">{r.sale_price ?? '—'}</td>
                <td className="px-2 py-1">{r.sitio || '—'}</td>
                <td className="px-2 py-1 tabular-nums text-right">{r.page ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RawJsonView({
  payload,
  onCopy,
  onSave,
  copyStatus,
  saving,
  savedPath,
  saveError,
}: {
  payload: any;
  onCopy: () => void;
  onSave: () => void;
  copyStatus: 'idle' | 'copied';
  saving: boolean;
  savedPath: string | null;
  saveError: string | null;
}) {
  const json = useMemo(() => JSON.stringify(payload, null, 2), [payload]);
  return (
    <div className="bg-white rounded border border-gray-200 max-w-6xl mx-auto flex flex-col" style={{ minHeight: 400 }}>
      <div className="flex-shrink-0 border-b border-gray-200 p-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-gray-500">
          {Object.keys(payload).length} bloques · {json.length.toLocaleString()} chars
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onCopy}
            className={`px-3 py-1.5 text-xs rounded border ${
              copyStatus === 'copied'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-gray-300 hover:bg-gray-50 text-gray-700'
            }`}
          >
            <Copy className="w-3 h-3 inline-block mr-1" />
            {copyStatus === 'copied' ? 'Copied' : 'Copy to clipboard'}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            className="px-3 py-1.5 text-xs rounded border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 disabled:opacity-60"
          >
            <Save className="w-3 h-3 inline-block mr-1" />
            {saving ? 'Saving…' : 'Save to disk'}
          </button>
        </div>
      </div>
      {(savedPath || saveError) && (
        <div className="flex-shrink-0 border-b border-gray-200 px-3 py-2 text-xs">
          {savedPath && (
            <div className="text-emerald-700">
              Guardado en:{' '}
              <span className="font-mono break-all select-all">{savedPath}</span>
              <span className="text-gray-500 ml-2">(path copiado al clipboard)</span>
            </div>
          )}
          {saveError && <div className="text-red-700 break-all">Error: {saveError}</div>}
        </div>
      )}
      <div className="flex-1 overflow-auto p-3">
        <pre className="text-[11px] font-mono text-gray-800 whitespace-pre-wrap break-all">{json}</pre>
      </div>
    </div>
  );
}

export default ProductDetailModal;
